/* eslint-disable @typescript-eslint/ban-types */
import 'isomorphic-unfetch'
import { EMethods } from './apiTypes'
import { json2qs } from '../utils/json2qs'
import { serialize } from 'object-to-formdata'
import { notification } from 'antd'
import {
    ForbiddenDescription,
    InternalServerErrorDescription,
    InternalServerErrorDescriptionWithMessage,
    NotFoundDescription,
    UnauthorizedDescription
} from 'shared/components/Notification/descriptions'

const HOST = process.env.REACT_APP_HOST || ''

const getFilenameFromContentDisposition = (response: Response) => {
    const contentDisposition = response.headers.get('Content-Disposition')
    const foundFilenames = contentDisposition ? contentDisposition.match(/filename="(.*)"/) : null
    return foundFilenames !== null && foundFilenames.length >= 1 ? foundFilenames[1] : null
}

const handleResponseFile = (response: Response, file: boolean | string) => {
    return response.blob().then((blob) => {
        const defaultFileName = typeof file === 'string' ? file : 'DownloadedFile'
        const url = window.URL.createObjectURL(blob)
        const fileName = getFilenameFromContentDisposition(response) || defaultFileName

        const a = document.createElement('a')
        a.href = url
        a.download = fileName
        document.body.appendChild(a)
        a.click()
        a.remove()

        return response
    })
}

function handleResponse(response: Response, file?: boolean | string) {
    if (response.ok) {
        if (file) {
            return handleResponseFile(response, file)
        }

        const accessToken = response.headers.get('AccessToken')
        const refreshToken = response.headers.get('RefreshToken')

        if (accessToken) {
            localStorage.setItem('accessToken', accessToken)
        }

        if (refreshToken) {
            localStorage.setItem('refreshToken', refreshToken)
        }

        const contentType = response.headers.get('content-type')
        if (contentType && contentType.indexOf('application/json') !== -1) {
            return response.json().then((json) => json)
        } else {
            return response.text().then(() => ({}))
        }
    } else {
        // что-то пошло не так - сброс на страницу авторизации:
        switch (response.status) {
            case 401:
                notification['error']({
                    message: 'Ошибка авторизации 401',
                    description: <UnauthorizedDescription />,
                    duration: 5
                })

                setTimeout(() => {
                    if (typeof window !== 'undefined' && window.location.pathname !== '/') {
                        window.location.href = '/'
                    }
                    localStorage.clear()
                }, 5000)
                break
            case 403:
                notification['error']({
                    message: 'Ошибка 403',
                    description: <ForbiddenDescription />,
                    duration: 5
                })
                break
            case 404:
                notification['error']({
                    message: 'Ошибка 404',
                    description: <NotFoundDescription />,
                    duration: 5
                })
                break
            case 500:
                response
                    .text()
                    .then((text) => {
                        let errorMessage = 'Неизвестная ошибка'
                        try {
                            const data = text ? JSON.parse(text) : null
                            errorMessage = data?.error || errorMessage
                        } catch (e) {
                            console.error('Ошибка парсинга JSON:', e)
                            errorMessage = 'Ошибка парсинга данных с сервера'
                        }

                        notification['error']({
                            message: 'Ошибка 500',
                            description: (
                                <InternalServerErrorDescriptionWithMessage
                                    errorMessage={errorMessage}
                                />
                            ),
                            duration: 10
                        })
                    })
                    .catch((error) => {
                        console.error('Ошибка парсинга ответа', error)
                        notification['error']({
                            message: 'Ошибка 500',
                            description: <InternalServerErrorDescription />,
                            duration: 10
                        })
                    })
                break
            default:
                break
        }

        return response.json().then((json) => {
            return Promise.reject(
                Object.assign({}, json, {
                    status: response.status,
                    statusText: response.statusText
                })
            )
        })
    }
}

export interface IHeaders {
    Accept: string
    'Content-Type'?: string
    Application?: string
    User?: string
}

export interface IRequestParams {
    url: string
    host?: string
    method: EMethods
    type?: string
    headers?: object
    data?: object | string
    file?: boolean | string
    isNotAuthorized?: boolean
    signal?: AbortSignal
}

export type TRequest = <T>(params: IRequestParams) => Promise<T>
export type TRequestMethod = <T>(params: Omit<IRequestParams, 'method'>) => Promise<T>

export const request: TRequest = (params) => {
    const action: TRequest = (params) => {
        const method = params.method || 'GET'
        const isMultipartFormData = params.type === 'multipart/form-data'

        let qs = ''
        let body: BodyInit | null = null
        const accessToken = localStorage.getItem('accessToken')
        const refreshToken = localStorage.getItem('refreshToken')

        const defaultHeaders = {
            Accept: '*/*'
        }
        const userHeaders = params.headers || {}
        const headers: Record<string, string> = {
            ...defaultHeaders,
            ...userHeaders
        }

        if (!isMultipartFormData) {
            headers['Content-Type'] = 'application/json'
        }

        if (accessToken) {
            headers['Accesstoken'] = accessToken
        }

        if (refreshToken) {
            headers['Refreshtoken'] = refreshToken
        }

        const data = params.data

        if (isMultipartFormData) {
            body = serialize(data)
        } else if (['GET'].indexOf(method) > -1) {
            // GET
            const query = json2qs(data)
            qs = query ? `?${query}` : ''
        } else {
            // POST or PUT
            body = JSON.stringify(data)
        }

        const url = (params.host !== undefined ? params.host : HOST) + params.url + qs

        return fetch(url, { method, headers, body, signal: params.signal }).then((response) =>
            handleResponse(response, params.file)
        )
    }

    return action(params)
}

export const get: TRequestMethod = (params) =>
    request(Object.assign({ method: EMethods.GET }, params))
export const post: TRequestMethod = (params) =>
    request(Object.assign({ method: EMethods.POST }, params))
export const put: TRequestMethod = (params) =>
    request(Object.assign({ method: EMethods.PUT }, params))
export const del: TRequestMethod = (params) =>
    request(Object.assign({ method: EMethods.DELETE }, params))
export const patch: TRequestMethod = (params) =>
    request(Object.assign({ method: EMethods.PATCH }, params))
